import React, { useEffect, useRef, useState } from "react";
import WhatsAppAPI from "../../api/WhatsAppAPI";

const OutgoingCalls = ({
  userDetail,
  socket,
  peerConnectionRef,
  remoteAudioRef,
  localStreamRef,
  onEndUI,
  callData
}) => {
  const [inCall, setInCall] = useState(false);
  const [status, setStatus] = useState("Calling...");
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);

  const durationIntervalRef = useRef(null);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs < 10 ? "0" : ""}${secs}s`;
  };

  const startTimer = () => {
    durationIntervalRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    clearInterval(durationIntervalRef.current);
  };

  const toggleMute = () => {
    const stream = localStreamRef.current;
    if (stream) {
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

    
  const endCall = () => {
    // console.log("[📞] Ending call...");
    stopTimer();
    setStatus("Call Ended");
    setInCall(false);

    try {
      if (peerConnectionRef.current) {
        peerConnectionRef.current.getSenders().forEach((sender) => {
          if (sender.track) sender.track.stop();
        });
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
        localStreamRef.current = null;
      }

      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = null;
      }
    } catch (err) {
      console.error("Error during cleanup:", err);
    }

    if (onEndUI) onEndUI();
  };


  const handleRemoteSDP = async (remoteSDP) => {
    try {
      if (!remoteSDP || peerConnectionRef.current?.remoteDescription) {
        console.warn("[⚠️] Remote SDP missing or already set");
        return;
      }

      const remoteDesc = new RTCSessionDescription({
        type: "answer",
        sdp: remoteSDP,
      });

      await peerConnectionRef.current.setRemoteDescription(remoteDesc);
      // console.log("[✅] Remote SDP set successfully");
    } catch (err) {
      console.error("[❌] handleRemoteSDP error:", err);
    }
  };

  useEffect(() => {
    const handleCallEvent = async ({ type, data }) => {
      // console.log("type, data",type, data)
      if (
        type === "call" &&
        data.direction === "BUSINESS_INITIATED" &&
        data.event === "connect" &&
        data.sdp
      ) {
        // console.log("[📲] Received SDP Answer via socket:", data);
        await handleRemoteSDP(data.sdp);
      }
      if (
        type === "call" &&
        data.direction === "BUSINESS_INITIATED" &&
        data.event === "terminate"
      ) {
        endCall();
      }

    };

    const handleCallStatus = ({ data }) => {
      // console.log("[📲] whatsapp_statuses:", data);

      if (data.status === "RINGING") {
        setStatus("Ringing...");
      } else if (data.status === "ACCEPTED") {
        setStatus("Call Accepted");
        setInCall(true);
        startTimer();
      } else if (["COMPLETED", "TERMINATE"].includes(data.status)) {
        endCall();
      }
    };

    socket.on("whatsapp_call_event", handleCallEvent);
    socket.on("whatsapp_statuses", handleCallStatus);

    return () => {
      socket.off("whatsapp_call_event", handleCallEvent);
      socket.off("whatsapp_statuses", handleCallStatus);
    };
  }, [socket]);

const handleReject = async () => {
    try {
      await WhatsAppAPI.rejectCall({
        call_id: callData?.call_id,
        business_number: callData?.business_number,
      });
    } catch (err) {
      console.error("Reject error:", err);
    }
    endCall();
  };
  const styles = {
    container: {
      position: "fixed",
      bottom: 20,
      right: 20,
      width: 320,
      height: 480,
      background: "#111",
      borderRadius: 16,
      boxShadow: "0 0 20px rgba(0,0,0,0.5)",
      zIndex: 9999,
      padding: 20,
      color: "#fff",
      textAlign: "center",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
    avatar: {
      width: 80,
      height: 80,
      backgroundColor: "#0f8d7d",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 32,
      fontWeight: "bold",
      margin: "0 auto 20px",
    },
    callerInfo: {
      marginBottom: 20,
    },
    buttons: {
      display: "flex",
      gap: 20,
      justifyContent: "center",
    },
    button: {
      padding: "10px 20px",
      fontSize: 14,
      border: "none",
      borderRadius: "30px",
      minWidth: 90,
      cursor: "pointer",
    },
    decline: { backgroundColor: "red", color: "#fff" },
    mute: { backgroundColor: "gray", color: "#fff" },
    status: {
      fontStyle: "italic",
      color: "#ccc",
      marginTop: 8,
    },
  };

  return (
    <div style={styles.container}>
      <audio ref={remoteAudioRef} autoPlay playsInline />
      <div style={styles.avatar}>
        {userDetail?.contactname?.[0]?.toUpperCase() || "U"}
      </div>
      <div style={styles.callerInfo}>
        <h4>Calling: {userDetail?.full_number}</h4>
        <p>{userDetail?.contactname || "Unknown"}</p>
        <p style={styles.status}>
          {inCall ? formatDuration(callDuration) : status}
        </p>
      </div>
      <div style={styles.buttons}>
        <button
          style={{ ...styles.button, ...styles.decline }}
          onClick={handleReject}
        >
          End
        </button>
        <button
          style={{ ...styles.button, ...styles.mute }}
          onClick={toggleMute}
        >
          {isMuted ? "Unmute" : "Mute"}
        </button>
      </div>
    </div>
  );
};

export default OutgoingCalls;
